import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';
import { Eye, EyeOff, Mail, Shield, ArrowLeft, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { getCartCount } = useCart();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  
  // 2FA state
  const [stage, setStage] = useState('credentials'); // 'credentials' | '2fa'
  const [maskedEmail, setMaskedEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputs = useRef([]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password })
      });
      const data = await response.json();

      if (response.ok && data.requires_2fa) {
        setMaskedEmail(data.masked_email || formData.email);
        setStage('2fa');
        setResendCooldown(30);
        setTimeout(() => codeInputs.current[0]?.focus(), 200);
      } else {
        setError(data.detail || 'Giriş başarısız. Email veya şifre hatalı.');
      }
    } catch (error) {
      setError('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const newCode = [...code];
    newCode[idx] = val.slice(-1);
    setCode(newCode);
    if (val && idx < 5) codeInputs.current[idx + 1]?.focus();
  };
  
  const handleCodeKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      codeInputs.current[idx - 1]?.focus();
    }
  };
  
  const handleCodePaste = (e) => {
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const newCode = ['', '', '', '', '', ''];
    for (let i = 0; i < pasted.length; i++) newCode[i] = pasted[i];
    setCode(newCode);
    codeInputs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Lütfen 6 haneli kodu girin');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, code: fullCode })
      });
      const data = await response.json();
      
      if (response.ok) {
        // Primary auth: real JWT in localStorage (cross-browser safe).
        localStorage.setItem('userToken', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        window.dispatchEvent(new Event('loginStateChanged'));
        navigate('/profile');
      } else {
        setError(data.detail || 'Kod geçersiz');
        setCode(['', '', '', '', '', '']);
        codeInputs.current[0]?.focus();
      }
    } catch {
      setError('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    setError('');
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/resend-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      const d = await r.json();
      if (r.ok) {
        setResendCooldown(30);
        setCode(['', '', '', '', '', '']);
        codeInputs.current[0]?.focus();
      } else {
        setError(d.detail || 'Kod gönderilemedi');
      }
    } catch {
      setError('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title="Üye Girişi - Cep Premium" description="Cep Premium üye giriş sayfası" />
      <Header cartCount={getCartCount()} />

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">
          {stage === 'credentials' ? (
            <>
              <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">Üye Girişi</h1>
              <p className="text-center text-gray-600 mb-8">Hesabınıza giriş yapın</p>

              {error && (
                <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Adresi</label>
                  <input
                    type="email" required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="login-email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                    placeholder="ornek@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Şifre</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'} required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      data-testid="login-password"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 pr-12"
                      placeholder="••••••••"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input type="checkbox" checked={formData.rememberMe}
                      onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
                      className="rounded border-gray-300 text-red-600 focus:ring-red-600" />
                    <span className="ml-2 text-sm text-gray-600">Beni Hatırla</span>
                  </label>
                  <Link to="/forgot-password" className="text-sm text-red-600 hover:text-red-700">
                    Şifremi Unuttum?
                  </Link>
                </div>
                <button type="submit" disabled={loading} data-testid="login-submit"
                  className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  {loading ? 'Gönderiliyor...' : 'Giriş Yap'}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  Hesabınız yok mu?{' '}
                  <Link to="/register" className="text-red-600 font-semibold hover:text-red-700">Üye Ol</Link>
                </p>
              </div>
            </>
          ) : (
            /* ============ 2FA CODE STAGE ============ */
            <>
              <button
                onClick={() => { setStage('credentials'); setError(''); setCode(['','','','','','']); }}
                data-testid="2fa-back-btn"
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-600 mb-4"
              >
                <ArrowLeft size={16} /> Geri dön
              </button>

              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <Shield className="text-red-600" size={32} />
                </div>
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Doğrulama Kodunu Girin</h1>
                <p className="text-sm text-gray-600 mb-1">
                  <Mail size={14} className="inline mr-1" />
                  <span data-testid="2fa-masked-email" className="font-semibold">{maskedEmail}</span>
                </p>
                <p className="text-sm text-gray-600">adresine 6 haneli kod gönderdik</p>
              </div>

              {error && (
                <div data-testid="2fa-error" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleVerify}>
                <div className="flex gap-2 justify-center mb-6" onPaste={handleCodePaste}>
                  {code.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => (codeInputs.current[i] = el)}
                      type="text" inputMode="numeric" maxLength={1}
                      value={d}
                      onChange={(e) => handleCodeChange(i, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(i, e)}
                      data-testid={`2fa-code-${i}`}
                      className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                    />
                  ))}
                </div>

                <button type="submit" disabled={loading || code.join('').length !== 6}
                  data-testid="2fa-verify-btn"
                  className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  {loading ? 'Doğrulanıyor...' : 'Doğrula ve Giriş Yap'}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">Kod gelmedi mi?</p>
                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || loading}
                  data-testid="2fa-resend-btn"
                  className="text-red-600 font-semibold text-sm mt-1 hover:text-red-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {resendCooldown > 0 ? `Yeni kod (${resendCooldown} sn)` : 'Yeni Kod Gönder'}
                </button>
              </div>

              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-xs text-yellow-800">
                  💡 Kodu email kutunuzda bulamadıysanız <strong>spam/gereksiz</strong> klasörünü kontrol edin. Kod 10 dakika geçerlidir.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LoginPage;
